// University of California, Merced: public college page (#2596), official facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - UC Merced's Common Data Set 2025-2026, the Excel file posted by the
//    Center of Institutional Effectiveness (cie.ucmerced.edu, External
//    Reporting). The KB pointed at a 2023-2024 copy on another college's
//    archive; the campus's own newer file replaces it. Read: C1
//    applicants/admits/enrollees for Fall 2025 with the residency table,
//    C8A (no use of SAT or ACT), C21/C22 (no early decision, no early
//    action). Its section G leaves G5 blank, so costs come from the
//    financial aid office. The campus site sits behind a bot wall, so every
//    ucmerced.edu page was read in a browser.
//  - admissions.ucmerced.edu first-year requirements (SAT/ACT statement).
//    Quirk: the admissions Apply page shows a "Test Optional" label next to
//    the deadline, but the requirements page and the CDS both say scores are
//    not considered for admission. The page states the requirements-page
//    policy; the label is flagged for the coordinator, not repeated here.
//  - UC Admissions (systemwide) Dates & deadlines: the fall 2027 filing
//    period and decision month, which apply to every UC campus.
//  - admissions.ucmerced.edu SJV PRIME+ admissions criteria (BS to MD).
//  - financialaid.ucmerced.edu cost of attendance, 2026-2027.
//  - College Scorecard school page, id 445188 (net price by family income;
//    the API was rate-limited, so the figures come from the official page).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UC Merced Common Data Set 2025-2026',
        url: 'https://cie.ucmerced.edu/sites/g/files/ufvvjh616/f/page/documents/cds_2025-26_uc_merced.xlsx',
        read: '2026-09-30',
    },
    requirements: {
        label: 'UC Merced Admissions, First-Year Requirements',
        url: 'https://admissions.ucmerced.edu/first-year/requirements',
        read: '2026-09-30',
    },
    ucDates: {
        label: 'UC Admissions, Dates and Deadlines',
        url: 'https://admission.universityofcalifornia.edu/how-to-apply/applying-as-a-first-year/dates-and-deadlines.html',
        read: '2026-09-30',
    },
    bsmd: {
        label: 'UC Merced Admissions, SJV PRIME+ Admissions Criteria',
        url: 'https://admissions.ucmerced.edu/bs-md/admissions-criteria-and-application-process',
        read: '2026-09-30',
    },
    cost: {
        label: 'UC Merced Financial Aid, Cost of Attendance',
        url: 'https://financialaid.ucmerced.edu/cost-attendance',
        read: '2026-09-30',
    },
    ucGuide: {
        label: 'UC Admissions, Quick Reference Guide to UC Admissions (August 2026)',
        url: 'https://admission.universityofcalifornia.edu/counselors/_files/documents/quick-reference-guide-to-uc-admissions.pdf',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?445188-University-of-California-Merced',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-california-merced',
    kbId: 'university_of_california_merced',
    name: 'University of California, Merced',
    shortName: 'UC Merced',
    location: 'Merced, CA',
    control: 'Public',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (UC Merced brand standards); decoration only.
    branding: {
        primary: '#002856',
        secondary: '#DAA900',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_california_merced_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 47985,
        admitted: 46564,
        enrolled: 1982,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'California residents', applied: 43388, admitted: 41849 },
            { group: 'Out-of-state', applied: 1365, admitted: 1172 },
            { group: 'International', applied: 4386, admitted: 3571 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UC Merced\'s Common Data Set reports one first-year acceptance rate for the whole campus, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'UC Merced has no early decision or early action plan. The Common Data Set answers no to both.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'One UC Merced program has its own application on top of the UC application:',
        rows: [
            { school: 'SJV PRIME+ BS to MD', requirement: 'Submit your UC application by November 20, 2026, earlier than the systemwide deadline, with a qualifying major (Biological Sciences, Bioengineering, Chemistry or Public Health) and the BS to MD track selected. You need at least a 3.6 UC GPA, a supplemental application and two letters of recommendation, due the first Friday in December.' },
        ],
        note: 'The program looks for applicants with deep ties to the San Joaquin Valley. Only first-year applicants who apply on time directly to UC Merced are reviewed for it.',
        source: 'bsmd',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application window', value: 'Oct 1 to Nov 30' },
        rows: [
            { plan: 'UC application', deadline: 'November 30', decision: 'March 1 to 31', binding: false },
        ],
        note: 'One UC application covers every UC campus. It opens August 1, 2026, and the fall 2027 filing period runs October 1 to November 30, 2026. Some campuses send decisions before March. If you are admitted, your Statement of Intent to Register is due May 1.',
        source: 'ucDates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'UC Merced will not consider SAT or ACT scores for admission or scholarships, so scores are not considered even if you send them. If you do send them, they may be used another way to meet minimum eligibility requirements, or for course placement after you enroll.',
        source: 'requirements',
        cdsLine: 'The Common Data Set agrees: it answers no to using SAT or ACT scores in admission decisions.',
        cdsWhere: 'section C8A',
    },

    // Changes already in effect for this cycle (#2651 review): they apply to
    // courses taken from fall 2026 on, so to this year's seniors too.
    currentChanges: [
        { topic: 'Math', line: 'A statistics or additional math course taken in fall 2026 or later no longer makes up for a missing or low grade in Algebra II or Math III. It still counts as an extra year of math. Seniors applying this fall: this already applies to courses you take this year.', source: 'ucGuide' },
        { topic: 'Math', line: 'From fall 2026, AP Statistics no longer meets two years of the UC math requirement. AP Precalculus (with a score of 4 or 5), AP Calculus AB, AP Calculus BC and IB HL Mathematics Analysis and Approaches still do.', source: 'ucGuide' },
    ],
    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'ucDates',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [17024, 17024] },
            { item: 'Additional nonresident tuition', amounts: [0, 39270] },
            { item: 'Housing (on campus)', amounts: [15154, 15154] },
            { item: 'Food', amounts: [8026, 8026] },
            { item: 'Books and supplies', amounts: [1148, 1148] },
            { item: 'Transportation', amounts: [1412, 1412] },
            { item: 'Personal expenses', amounts: [2814, 2814] },
            { item: 'Health insurance', amounts: [2996, 2996] },
            { item: 'Loan fee', amounts: [80, 80] },
        ],
        totals: [48654, 87924],
        note: 'UC Merced\'s budget for undergraduates who first enrolled in fall 2026, living on campus. The budget for students entering in fall 2027 is not posted yet. Under UC\'s Tuition Stability Plan, tuition is set for each entering class and then stays flat for up to six years.',
        source: 'cost',
        netPrice: {
            average: 11583,
            byIncome: [
                { income: '$0 to $30,000', amount: 7387 },
                { income: '$30,001 to $48,000', amount: 7141 },
                { income: '$48,001 to $75,000', amount: 10285 },
                { income: '$75,001 to $110,000', amount: 14069 },
                { income: 'Over $110,000', amount: 30779 },
            ],
            note: 'What first-year, full-time in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UC Merced\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
